/**
 * SSOLanding — Phase 1 enterprise SSO landing page (ADR-022).
 *
 * Mounted at ``/auth/sso-landing``. The svdy-authorization OIDC
 * callback redirects here with a token bundle in the URL fragment::
 *
 *   /auth/sso-landing?token=<legacy>#access_token=<a>&refresh_token=<r>&expires_in=<n>&token_type=Bearer
 *
 * On success:
 *   1. Parse the fragment.
 *   2. Validate it has access_token + refresh_token + expires_in.
 *   3. Call AuthContext.signIn(...) — same path the OAuth2 PKCE
 *      callback uses, so downstream behaviour (org selection,
 *      profile fetch, refresh scheduling) is identical.
 *   4. window.history.replaceState() to scrub the URL of secrets
 *      BEFORE any further navigation (so the browser back-button
 *      can't revive the fragment).
 *   5. Navigate to ``/auth/select-org`` (the post-sign-in shell
 *      that picks the active org).
 *
 * On failure:
 *   - Either ``?sso_error=<code>`` is present (the OIDC callback
 *     refused at some stage), OR the fragment is missing /
 *     malformed (worker landed here directly).
 *   - Render an inline Alert with a friendly message keyed off
 *     the error code; offer a "Sign in with password" fallback
 *     button that re-runs the standard OAuth2 PKCE redirect.
 *
 * Why the fragment (not query):
 *   Fragments are NOT sent to the server in the next request, so
 *   the access_token won't appear in upstream access logs / proxy
 *   logs / Referer headers. Browser exposes it to JS via
 *   ``location.hash``; we consume it client-side then scrub.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../auth/AuthContext';
import { redirectToAuthorize } from '../../auth/authService';

/** Map sso_error codes from the OIDC callback to i18n keys.
 *  Unknown codes fall through to a generic message. */
const SSO_ERROR_KEYS: Record<string, string> = {
  redirect_not_allowed: 'auth:sso.errors.redirectNotAllowed',
  sso_not_enabled: 'auth:sso.errors.ssoNotEnabled',
  no_active_config: 'auth:sso.errors.noActiveConfig',
  email_domain_mismatch: 'auth:sso.errors.emailDomainMismatch',
  config_unreachable: 'auth:sso.errors.configUnreachable',
  config_incomplete: 'auth:sso.errors.configIncomplete',
  discovery_failed: 'auth:sso.errors.discoveryFailed',
  token_exchange_failed: 'auth:sso.errors.tokenExchangeFailed',
  no_id_token: 'auth:sso.errors.noIdToken',
  jwks_unreachable: 'auth:sso.errors.jwksUnreachable',
  id_token_invalid: 'auth:sso.errors.idTokenInvalid',
  no_email_claim: 'auth:sso.errors.noEmailClaim',
  malformed_email: 'auth:sso.errors.malformedEmail',
  no_matching_account: 'auth:sso.errors.noMatchingAccount',
  account_inactive: 'auth:sso.errors.accountInactive',
  missing_state: 'auth:sso.errors.missingState',
  state_invalid: 'auth:sso.errors.stateInvalid',
  missing_code: 'auth:sso.errors.missingCode',
};

/** Codes that come back as ``idp_<vendor_code>`` — IdP-side
 *  declines / cancels. Not enumerated; surfaced generically. */
const IDP_DECLINE_PREFIX = 'idp_';

const SSOLanding: React.FC = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const consumed = useRef(false);
  const [phase, setPhase] = useState<'consuming' | 'error' | 'redirecting'>(
    'consuming',
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Parse fragment + query ONCE on mount.
  const inputs = useMemo(() => {
    const fragment = window.location.hash.replace(/^#/, '');
    const fragmentParams = new URLSearchParams(fragment);
    const queryParams = new URLSearchParams(window.location.search);
    return {
      accessToken: fragmentParams.get('access_token'),
      refreshToken: fragmentParams.get('refresh_token'),
      expiresIn: fragmentParams.get('expires_in'),
      idToken: fragmentParams.get('id_token') ?? undefined,
      ssoError: queryParams.get('sso_error'),
    };
  }, []);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    if (inputs.ssoError) {
      setErrorCode(inputs.ssoError);
      setPhase('error');
      // Scrub the query param so the back button doesn't re-fire
      // the error rendering on bounce.
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (
      !inputs.accessToken ||
      !inputs.refreshToken ||
      !inputs.expiresIn
    ) {
      // Worker landed here directly without a token bundle. Treat
      // as a generic error — the most common cause is a copy-paste
      // of the URL after the fragment was already consumed once.
      setErrorCode('missing_token_bundle');
      setPhase('error');
      return;
    }

    const expiresIn = parseInt(inputs.expiresIn, 10);
    if (Number.isNaN(expiresIn) || expiresIn <= 0) {
      setErrorCode('malformed_token_bundle');
      setPhase('error');
      return;
    }

    // Hand off to AuthContext — same code path as the OAuth2 PKCE
    // /auth/callback so org selection, profile fetch, and refresh
    // scheduling all match.
    signIn(
      inputs.accessToken,
      inputs.refreshToken,
      expiresIn,
      inputs.idToken,
    );

    // Scrub the URL of secrets BEFORE we navigate so the browser
    // history doesn't retain access_token in the back-stack.
    window.history.replaceState({}, '', '/auth/sso-landing');

    setPhase('redirecting');
    navigate('/auth/select-org', { replace: true });
  }, [inputs, signIn, navigate]);

  if (phase === 'error') {
    const friendly = (() => {
      if (!errorCode) return t('auth:sso.errors.generic');
      if (errorCode in SSO_ERROR_KEYS) {
        return t(SSO_ERROR_KEYS[errorCode]);
      }
      if (errorCode.startsWith(IDP_DECLINE_PREFIX)) {
        return t('auth:sso.errors.idpDeclined');
      }
      if (errorCode === 'missing_token_bundle') {
        return t('auth:sso.errors.missingTokenBundle');
      }
      if (errorCode === 'malformed_token_bundle') {
        return t('auth:sso.errors.malformedTokenBundle');
      }
      return t('auth:sso.errors.genericWithCode', { code: errorCode });
    })();

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          px: 2,
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 480, width: '100%' }}>
          <Alert severity="error" variant="outlined">
            <AlertTitle>{t('auth:sso.failedTitle')}</AlertTitle>
            {friendly}
          </Alert>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => redirectToAuthorize()}
              sx={{ borderRadius: 1, textTransform: 'none' }}
            >
              {t('auth:sso.signInWithPassword')}
            </Button>
          </Stack>
          {errorCode ? (
            <Typography variant="caption" color="text.secondary">
              Error code: <code>{errorCode}</code>
            </Typography>
          ) : null}
        </Stack>
      </Box>
    );
  }

  // 'consuming' / 'redirecting' — same loading state. The
  // useEffect resolves quickly enough that flashing isn't an
  // issue; we render one progress UI for the whole hand-off.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Completing single sign-on…
      </Typography>
    </Box>
  );
};

export default SSOLanding;
